import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs, orderBy, query, limit, startAfter, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import AdminNav from './AdminNav';
import { FaSearch, FaEye, FaCheck, FaTruck, FaTimes, FaEdit, FaEnvelope, FaTag } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import ShippingLabelCreator from './ShippingLabelCreator';

const Container = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 1.5rem;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const TableHead = styled.thead`
  background-color: #f5f5f5;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f9f9f9;
  }
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #555;
`;

const TableCell = styled.td`
  padding: 1rem;
  color: #333;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
    if (props.status === 'completed') return '#d1e7dd';
    if (props.status === 'processing') return '#fff3cd';
    if (props.status === 'cancelled') return '#f8d7da';
    return '#e2e3e5';
  }};
  color: ${props => {
    if (props.status === 'completed') return '#0f5132';
    if (props.status === 'processing') return '#856404';
    if (props.status === 'cancelled') return '#842029';
    return '#41464b';
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: none;
  background-color: #e2f0ff;
  color: #0d6efd;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #cfe2ff;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const EmptyTitle = styled.h3`
  color: #333;
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  color: #666;
  margin-bottom: 1rem;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const PageButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ddd;
  background-color: ${props => props.active ? '#C41E3A' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  cursor: pointer;
  
  &:first-child {
    border-radius: 4px 0 0 4px;
  }
  
  &:last-child {
    border-radius: 0 4px 4px 0;
  }
  
  &:hover {
    background-color: ${props => props.active ? '#A01930' : '#f5f5f5'};
  }
  
  &:disabled {
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: #f1f3f4;
  color: #333;
  
  &:hover:not(:disabled) {
    background-color: #e2e3e5;
  }
`;

const SaveButton = styled(Button)`
  background-color: #C41E3A;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #A01930;
  }
`;

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [trackingInfo, setTrackingInfo] = useState({
    carrier: 'usps',
    trackingNumber: '',
    notes: ''
  });
  const [updating, setUpdating] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [emailStatus, setEmailStatus] = useState({ sending: false, success: false, error: null });
  const [shippingLabelMode, setShippingLabelMode] = useState(false);
  const ordersPerPage = 10;
  
  useEffect(() => {
    fetchOrders();
  }, []);
  
  const fetchOrders = async (searchTerm = '') => {
    try {
      setLoading(true);
      
      // Create query
      const ordersRef = collection(db, 'orders');
      let q = query(ordersRef, orderBy('createdAt', 'desc'), limit(ordersPerPage));
      
      // Execute query
      const snapshot = await getDocs(q);
      
      // Process results
      if (snapshot.empty) {
        setOrders([]);
        setHasMore(false);
      } else {
        const ordersList = [];
        snapshot.forEach(doc => {
          const data = doc.data();
          ordersList.push({
            id: doc.id,
            customer: data.customerInfo?.name || 'Guest',
            email: data.customerInfo?.email || 'N/A',
            date: data.createdAt,
            total: data.orderDetails?.total || data.total || 0,
            status: data.status || 'pending',
            items: data.orderDetails?.items || data.items || [],
            customerInfo: data.customerInfo || {},
            tracking: data.tracking || null
          });
        });
        
        setOrders(ordersList);
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        setHasMore(snapshot.docs.length === ordersPerPage);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchMoreOrders = async () => {
    if (!lastVisible) return;
    
    try {
      setLoading(true);
      
      // Create query
      const ordersRef = collection(db, 'orders');
      let q = query(
        ordersRef, 
        orderBy('createdAt', 'desc'), 
        startAfter(lastVisible),
        limit(ordersPerPage)
      );
      
      // Execute query
      const snapshot = await getDocs(q);
      
      // Process results
      if (snapshot.empty) {
        setHasMore(false);
      } else {
        const ordersList = [...orders];
        snapshot.forEach(doc => {
          const data = doc.data();
          ordersList.push({
            id: doc.id,
            customer: data.customerInfo?.name || 'Guest',
            email: data.customerInfo?.email || 'N/A',
            date: data.createdAt,
            total: data.orderDetails?.total || data.total || 0,
            status: data.status || 'pending',
            items: data.orderDetails?.items || data.items || [],
            customerInfo: data.customerInfo || {},
            tracking: data.tracking || null
          });
        });
        
        setOrders(ordersList);
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        setHasMore(snapshot.docs.length === ordersPerPage);
        setCurrentPage(currentPage + 1);
      }
    } catch (error) {
      console.error('Error fetching more orders:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: newStatus,
        updatedAt: new Date()
      });
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === orderId 
          ? { ...order, status: newStatus } 
          : order
      ));
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };
  
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };
  
  const openTrackingModal = (order) => {
    setCurrentOrder(order);
    if (order.tracking) {
      setTrackingInfo({
        carrier: order.tracking.carrier || 'usps',
        trackingNumber: order.tracking.trackingNumber || '',
        notes: order.tracking.notes || ''
      });
    } else {
      setTrackingInfo({
        carrier: 'usps',
        trackingNumber: '',
        notes: ''
      });
    }
    setModalOpen(true);
    setShippingLabelMode(false);
  };
  
  const openShippingLabelCreator = (order) => {
    console.log("Opening shipping label creator with order:", order);
    setCurrentOrder(order);
    setShippingLabelMode(true);
    console.log("shippingLabelMode set to:", true);
  };
  
  const closeTrackingModal = () => {
    setModalOpen(false);
    setCurrentOrder(null);
    setTrackingInfo({
      carrier: 'usps',
      trackingNumber: '',
      notes: ''
    });
    setShippingLabelMode(false);
  };
  
  const handleTrackingChange = (e) => {
    const { name, value } = e.target;
    setTrackingInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const saveTracking = async () => {
    if (!currentOrder || !trackingInfo.trackingNumber) return;
    
    try {
      setUpdating(true);
      
      const orderRef = doc(db, 'orders', currentOrder.id);
      
      // Update with tracking info and set status to shipped
      await updateDoc(orderRef, {
        tracking: {
          ...trackingInfo,
          addedAt: new Date()
        },
        status: 'shipped',
        updatedAt: new Date()
      });
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === currentOrder.id 
          ? { 
              ...order, 
              status: 'shipped', 
              tracking: {
                ...trackingInfo,
                addedAt: new Date()
              }
            } 
          : order
      ));
      
      // Send email notification if checkbox is checked
      if (sendEmail) {
        await sendShippingNotification(currentOrder, trackingInfo);
      }
      
      // Close modal
      closeTrackingModal();
      
    } catch (error) {
      console.error('Error saving tracking information:', error);
    } finally {
      setUpdating(false);
    }
  };
  
  const sendShippingNotification = async (order, tracking) => {
    try {
      setEmailStatus({ sending: true, success: false, error: null });
      
      // Use the full Netlify URL
      const netlifyUrl = 'https://benevolent-sfogliatella-83141e.netlify.app/.netlify/functions/send-shipping-notification';
      console.log("Sending email notification using URL:", netlifyUrl);
      
      const response = await fetch(netlifyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerEmail: order.email,
          customerName: order.customer,
          orderId: order.id,
          trackingNumber: tracking.trackingNumber,
          carrier: tracking.carrier,
          orderItems: order.items,
          shippingAddress: order.customerInfo
        }),
      });
      
      console.log("Email notification response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response from email notification:", errorText);
        throw new Error(`Error sending email notification: ${response.status} ${errorText}`);
      }
      
      // Try to parse the response as JSON, but handle non-JSON responses
      let responseText = await response.text();
      console.log("Raw email response (first 500 chars):", responseText.substring(0, 500));
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Error parsing JSON response:", parseError);
        throw new Error(`Invalid response from server. This usually indicates the Netlify function is not being called correctly.`);
      }
      
      console.log('Email notification sent:', result);
      setEmailStatus({ sending: false, success: true, error: null });
      
    } catch (error) {
      console.error('Error sending email notification:', error);
      setEmailStatus({ sending: false, success: false, error: error.message });
    }
  };
  
  const handleLabelCreated = async (labelInfo) => {
    if (!currentOrder || !labelInfo.trackingNumber) return;
    
    try {
      setUpdating(true);
      
      const orderRef = doc(db, 'orders', currentOrder.id);
      
      // Update with tracking info and set status to shipped
      await updateDoc(orderRef, {
        tracking: {
          carrier: labelInfo.carrier,
          trackingNumber: labelInfo.trackingNumber,
          trackingUrl: labelInfo.trackingUrl,
          labelUrl: labelInfo.labelUrl,
          addedAt: new Date()
        },
        status: 'shipped',
        updatedAt: new Date()
      });
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === currentOrder.id 
          ? { 
              ...order, 
              status: 'shipped', 
              tracking: {
                carrier: labelInfo.carrier,
                trackingNumber: labelInfo.trackingNumber,
                trackingUrl: labelInfo.trackingUrl,
                labelUrl: labelInfo.labelUrl,
                addedAt: new Date()
              }
            } 
          : order
      ));
      
      // Send email notification if checkbox is checked
      if (sendEmail) {
        await sendShippingNotification(currentOrder, {
          carrier: labelInfo.carrier,
          trackingNumber: labelInfo.trackingNumber,
          notes: ''
        });
      }
      
    } catch (error) {
      console.error('Error saving tracking information:', error);
    } finally {
      setUpdating(false);
    }
  };
  
  const getTrackingUrl = (carrier, trackingNumber) => {
    switch (carrier.toLowerCase()) {
      case 'usps':
        return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
      case 'ups':
        return `https://www.ups.com/track?tracknum=${trackingNumber}`;
      case 'fedex':
        return `https://www.fedex.com/apps/fedextrack/?tracknumbers=${trackingNumber}`;
      case 'dhl':
        return `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}`;
      default:
        return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    }
  };
  
  const filteredOrders = orders.filter(order => {
    const searchLower = searchQuery.toLowerCase();
    return (
      order.id.toLowerCase().includes(searchLower) ||
      order.customer.toLowerCase().includes(searchLower) ||
      order.email.toLowerCase().includes(searchLower) ||
      order.status.toLowerCase().includes(searchLower) ||
      (order.tracking?.trackingNumber && order.tracking.trackingNumber.toLowerCase().includes(searchLower))
    );
  });
  
  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  return (
    <Container>
      <AdminNav />
      
      <Header>
        <Title>Order Management</Title>
        <Subtitle>View and manage all customer orders</Subtitle>
      </Header>
      
      <SearchContainer>
        <SearchIcon>
          <FaSearch />
        </SearchIcon>
        <SearchInput 
          type="text" 
          placeholder="Search orders by ID, customer, email, status, or tracking number..." 
          value={searchQuery}
          onChange={handleSearch}
        />
      </SearchContainer>
      
      {loading && orders.length === 0 ? (
        <EmptyState>
          <EmptyTitle>Loading orders...</EmptyTitle>
        </EmptyState>
      ) : filteredOrders.length > 0 ? (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Order ID</TableHeader>
                <TableHeader>Customer</TableHeader>
                <TableHeader>Date</TableHeader>
                <TableHeader>Total</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Tracking</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <tbody>
              {filteredOrders.map(order => (
                <TableRow key={order.id}>
                  <TableCell>{order.id.substring(0, 8)}...</TableCell>
                  <TableCell>
                    <div>
                      <div>{order.customer}</div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>{order.email}</div>
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(order.date)}</TableCell>
                  <TableCell>{formatCurrency(order.total)}</TableCell>
                  <TableCell>
                    <Badge status={order.status}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {order.tracking?.trackingNumber ? (
                      <a 
                        href={getTrackingUrl(order.tracking.carrier, order.tracking.trackingNumber)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#0d6efd', textDecoration: 'none' }}
                      >
                        {order.tracking.trackingNumber}
                      </a>
                    ) : (
                      <span style={{ color: '#666', fontSize: '0.9rem' }}>Not shipped</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <ActionButtons>
                      <IconButton as={Link} to={`/orders/${order.id}`} title="View Order">
                        <FaEye />
                      </IconButton>
                      <IconButton 
                        onClick={() => openShippingLabelCreator(order)}
                        title="Create Shipping Label"
                      >
                        <FaTag />
                      </IconButton>
                      <IconButton 
                        onClick={() => openTrackingModal(order)}
                        title="Add/Edit Tracking"
                      >
                        <FaEdit />
                      </IconButton>
                      {order.status !== 'completed' && (
                        <IconButton 
                          onClick={() => updateOrderStatus(order.id, 'completed')}
                          title="Mark as Completed"
                        >
                          <FaCheck />
                        </IconButton>
                      )}
                      {order.status !== 'shipped' && order.status !== 'completed' && !order.tracking && (
                        <IconButton 
                          onClick={() => updateOrderStatus(order.id, 'shipped')}
                          title="Mark as Shipped"
                        >
                          <FaTruck />
                        </IconButton>
                      )}
                      {order.status !== 'cancelled' && (
                        <IconButton 
                          onClick={() => updateOrderStatus(order.id, 'cancelled')}
                          title="Cancel Order"
                        >
                          <FaTimes />
                        </IconButton>
                      )}
                    </ActionButtons>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
          
          {hasMore && (
            <Pagination>
              <PageButton onClick={fetchMoreOrders} disabled={loading}>
                Load More
              </PageButton>
            </Pagination>
          )}
        </>
      ) : (
        <EmptyState>
          <EmptyTitle>No orders found</EmptyTitle>
          <EmptyText>
            {searchQuery 
              ? `No orders match your search for "${searchQuery}"`
              : "There are no orders in the system yet."}
          </EmptyText>
        </EmptyState>
      )}
      
      {modalOpen && currentOrder && !shippingLabelMode && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {currentOrder.tracking ? 'Edit Tracking Information' : 'Add Tracking Information'}
              </ModalTitle>
              <CloseButton onClick={closeTrackingModal}>&times;</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label htmlFor="carrier">Shipping Carrier</Label>
              <Select
                id="carrier"
                name="carrier"
                value={trackingInfo.carrier}
                onChange={handleTrackingChange}
              >
                <option value="usps">USPS</option>
                <option value="ups">UPS</option>
                <option value="fedex">FedEx</option>
                <option value="dhl">DHL</option>
                <option value="other">Other</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="trackingNumber">Tracking Number</Label>
              <Input
                type="text"
                id="trackingNumber"
                name="trackingNumber"
                value={trackingInfo.trackingNumber}
                onChange={handleTrackingChange}
                placeholder="Enter tracking number"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Input
                type="text"
                id="notes"
                name="notes"
                value={trackingInfo.notes}
                onChange={handleTrackingChange}
                placeholder="Add any notes about this shipment"
              />
            </FormGroup>
            
            <FormGroup style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <input
                type="checkbox"
                id="sendEmail"
                checked={sendEmail}
                onChange={() => setSendEmail(!sendEmail)}
                style={{ marginRight: '0.5rem' }}
              />
              <Label htmlFor="sendEmail" style={{ margin: 0 }}>
                Send email notification to customer
              </Label>
            </FormGroup>
            
            {emailStatus.error && (
              <div style={{ color: '#C41E3A', marginBottom: '1rem', fontSize: '0.9rem' }}>
                Error sending email: {emailStatus.error}
              </div>
            )}
            
            <ButtonGroup>
              <CancelButton onClick={closeTrackingModal}>Cancel</CancelButton>
              <SaveButton 
                onClick={saveTracking} 
                disabled={updating || !trackingInfo.trackingNumber}
              >
                {updating ? 'Saving...' : 'Save & Mark as Shipped'}
              </SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
      
      {shippingLabelMode && currentOrder && (
        <>
          {console.log("Rendering ShippingLabelCreator component with order:", currentOrder)}
          <ShippingLabelCreator 
            order={currentOrder} 
            onClose={closeTrackingModal}
            onLabelCreated={handleLabelCreated}
          />
        </>
      )}
    </Container>
  );
};

export default OrderManagement;
